import * as React from 'react';
import moment, {Moment} from 'moment';
import styled, {css} from 'styled-components';
import {CourseObject} from 'api/course';
import {getImageByPreview} from 'api/library';
import Tag, {Container as TagContainer} from 'components/Tag';
import {transparentize} from 'polished';
import {IconCalendarSM, IconFireSM, IconLesson, IconTaskCount, IconUserSM, IconVideoCamera} from 'icons';
import {CardView} from "hooks/use-selected-view";
import {CourseFooter, CourseHeader} from "./TeacherCoursePreview";
import {navigateTo} from "../helpers/navigate-to";
import UserStore, {Role} from 'stores/UserStore';
import ModalCourseStudents from "./modals/CourseStudents";

export enum CoursePreviewVariant {
  Default = 'default',
}

export interface CoursePreviewProps extends CourseObject {
  variant?: CoursePreviewVariant;
  view?: CardView;
  renderHeader?: JSX.Element;
  renderFooter?: JSX.Element;
  opened?: boolean;
  studentsModalOpened?: boolean;
  studentModalClose?: () => void;
}

const Preview = styled.img`
  border-radius: 5px;
  object-fit: cover;
  height: 230px;
  width: 100%;
`;
const Info = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0.01em;
`;

const InfoContent = styled.div`
  display: grid;
  grid-template-columns: repeat(3, max-content);
  gap: 12.5px;
  color: ${({theme}) => transparentize(.6, theme.variables.color)}
`;

const InfoItem = styled.div`
  display: grid;
  grid-template-columns: repeat(2, max-content);
  gap: 6px;
  align-items: center;
  
  svg {
    max-height: 12px;
  }
`;

const InfoDate = styled.div`
  color: ${({theme}) => transparentize(.8, theme.variables.color)}
`;


const Tags = styled.div`
  flex-wrap: wrap;
  display: flex;
  
  ${TagContainer} {
    margin-top: 2px;
    margin-right: 4px;
    margin-bottom: 2px;
  }
`;

const Title = styled.a`
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  transition: color .3s;
  cursor: pointer;
  
  &:hover,
  &:focus-visible {
    color: ${({theme}) => theme.colors.primary};
  }
`;

const Settings = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Setting = styled.li`
  margin: 3px 0;
  align-items: center;
  display: flex;
  
  line-height: 16px;
  font-weight: 500;
  font-size: 12px;
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
`;

const SettingIcon = styled.div`
  display: grid;
  color: ${({theme}) => theme.colors.primary};
  margin-right: 6px;
`;

const Container = styled.article<Pick<CoursePreviewProps, 'variant' | 'view' | 'opened'>>`
  position: relative;
  max-width: 456px;
  width: 100%;
  border-radius: 5px;
  box-sizing: border-box;
  padding: 20px;
  gap: 10px;
  display: grid;
  
  ${({variant, theme}) => {
    switch (variant) {
      case CoursePreviewVariant.Default: return css`
          background-color: ${theme.variables.accentBackgroundColor};
        `;
    }
  }};
  
  ${({view}) => {
    switch (view) {
      case CardView.Tiled: return css`
          max-width: 456px;
        `;
      case CardView.List: return css`
          max-width: 100%;
        `;
      case CardView.ShortList: return css`
          max-width: 100%;
        `;
    }
  }};
  
  ${({view, opened}) => {
    switch (view) {
      case CardView.Tiled: return css`
          max-width: 456px;
      `;
      case CardView.List: return css`
          max-width: 100%;
          
          ${Preview} {
            height: 88px;
            width: 158px;
          }
      `;
      case CardView.ShortList: return css`
          padding: ${() => opened ? '20px' : '4px'};
          max-height: ${() => opened ? '350px' : '96px'};
          max-width: 100%;
        
          ${Preview} {
            height: 88px;
            width: 158px;
          }
        
          ${Title} {
            display: block;
            margin-right: 188px;
            margin-top: 8px;
          }
        
          ${CourseHeader} {
            ${() => !opened && css`
              position: absolute;
              top: 4px;
              right: 4px;
              z-index: 5;
              display: flex;
              justify-content: flex-end;
            `}
          }

          ${Tags} {
            margin-top: 8px;
          }
        
          ${Info},
          ${Settings},
          ${CourseFooter} {
            opacity: ${() => opened ? 1 : 0};
            pointer-events: none;
          }
        `;
    }
  }};
`;

const ContentList = styled.div`
  position: relative;
  display: flex;
  
  ${Preview} {
    margin-right: 19px;
  }
  
  ${Title} {
    font-size: 16px;
    font-weight: 600;
    line-height: 21px;
    letter-spacing: 0.01em;
  }
  
  ${Settings} {
    margin-top: 12px;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: 0.01em;
  }
  
  ${Info} {
    display: grid;
    gap: 12px;
    margin-top: 10px;
    margin-bottom: 22px;
  }
  
  ${Tags} {
    margin-bottom: 12px;
  }
`;

/**
 * This component renders a preview of the course.
 * @param {CoursePreviewProps} props
 * @return {React.ReactElement<CoursePreviewProps>}
 * @constructor
 */
function CoursePreview(
    props: CoursePreviewProps,
): React.ReactElement<CoursePreviewProps> {
  const [
    previewSrc, setPreviewSrc,
  ] = React.useState<string>(getImageByPreview(props.preview));

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element | undefined {
    return props.renderHeader;
  }

  /**
   * Render preview image.
   * @return {JSX.Element}
   */
  function renderPreview(): JSX.Element {
    return (
      <Preview
        src={previewSrc}
        onError={() => (
          setPreviewSrc(`${process.env.PUBLIC_URL}/images/not-found.png`)
        )}
      />
    );
  }

  /**
   * Render preview info.
   * @return {JSX.Element}
   */
  function renderInfo(arg?: 'date' | 'content'): JSX.Element {
    const createdDate = moment(props.createdAt).format('DD.MM.YYYY | HH:MM');

    let date = (arg === "date") || (arg === undefined)
        ? <InfoDate>Создан {createdDate}</InfoDate>
        : null;

    let content = (arg === "content") || (arg === undefined)
        ? (
          <InfoContent>
            <InfoItem>
              <IconLesson />
              {props.counters?.lesson}
            </InfoItem>
            <InfoItem>
              <IconTaskCount />
              {props.counters?.read}
            </InfoItem>
            <InfoItem>
              <IconVideoCamera />
              {props.counters?.webinar}
            </InfoItem>
          </InfoContent>
          )
        : null;

    return (
      <Info>
        {content}
        {date}
      </Info>
    );
  }

  /**
   * Render tags.
   * @return {JSX.Element | undefined}
   */
  function renderTags(): JSX.Element | undefined {
    return props.tags?.length
      ? (
          <Tags>
            {props.tags.map((tag, index) => (
              <Tag key={index}>
                {tag}
              </Tag>
            ))}
          </Tags>
        )
      : undefined;
  }

  /**
   * Render title.
   * @return {JSX.Element}
   */
  function renderTitle(): JSX.Element {
    /**
     * on title click handler
     */
    function onClickHandler() {
      const role = UserStore.state?.role;

      if (role === Role.Teacher) {
        navigateTo('courses.course.edit', {id: props.id})
      }
      else if (role === Role.Student) {
        navigateTo('courses.course.program', {id: props.id})
      }
    }

    return (
      <Title onClick={onClickHandler}>
        {props.title}
      </Title>
    );
  }

  /**
   * This function will render a list of course settings.
   * @return {JSX.Element}
   */
  function renderSettings(): JSX.Element {
    /**
     * Format date to label.
     * @param {moment.Moment | string} date
     * @return {string}
     */
    function formatDate(date: Moment | string): string {
      return moment(date).format('DD MMM YYYY');
    }

    /**
     * Render sales dates.
     * @return {JSX.Element}
     */
    function renderSalesDates(): JSX.Element | null {
      if (!props.salesDates) return null;

      /**
       * Get start date label.
       * @return {string}
       */
      function getStartDate(): string | null {
        return props.salesDates?.startDate
          ? formatDate(props.salesDates?.startDate)
          : null;
      }

      /**
       * Get over date label.
       * @return {string}
       */
      function getOverDate(): string | null {
        return props.salesDates?.overDate
          ? formatDate(props.salesDates?.overDate)
          : null;
      }

      /**
       * Get sales dates range count.
       * @return {number | null}
       */
      function getRangeCount(): number | null {
        return (props.salesDates?.startDate && props.salesDates?.overDate) ? (
          moment(props.salesDates.overDate)
              .diff(props.salesDates.startDate, 'day')
        ) : null;
      }

      /**
       * Get label.
       * @return {string}
       */
      function getLabel(): string {
        const startDate = getStartDate() || '';
        const overDate = getOverDate();
        const rangeCountDate = getRangeCount();

        let result:string = startDate;

        result = overDate
          ? `${result} - ${overDate}`
          : result;

        result = rangeCountDate
          ? `${result} | ${rangeCountDate} дней`
          : result;

        return result;
      }

      return (
        <Setting>
          <SettingIcon>
            <IconCalendarSM />
          </SettingIcon>
          {getLabel()}
        </Setting>
      );
    }

    return (
      <Settings>
        {renderSalesDates()}
        <Setting>
          <SettingIcon>
            <IconFireSM />
          </SettingIcon>
          Уже идет
        </Setting>
        <Setting>
          <SettingIcon>
            <IconUserSM />
          </SettingIcon>
          {props.students?.length}
        </Setting>
      </Settings>
    );
  }

  /**
   * Render footer.
   * @return {JSX.Element}
   */
  function renderFooter(): JSX.Element | undefined {
    return props.renderFooter;
  }

  function renderTiledView() {
    return (
      <React.Fragment>
        {renderHeader()}
        {renderPreview()}
        {renderInfo()}
        {renderTags()}
        {renderTitle()}
        {renderSettings()}
        {renderFooter()}
      </React.Fragment>
    )
  }

  function renderListView() {
    return (
      <React.Fragment>
        {renderHeader()}
        <ContentList>
          <div>
            {renderPreview()}
            {renderInfo('content')}
            {renderFooter()}
          </div>
          <div>
            {renderTags()}
            {renderTitle()}
            {renderInfo('date')}
            {renderSettings()}
          </div>
        </ContentList>
      </React.Fragment>
    )
  }

  function renderShortListView() {
    return (
      <React.Fragment>
        {renderHeader()}
        <ContentList>
          <div>
            {renderPreview()}
            {renderInfo('content')}
            {renderFooter()}
          </div>
          <div>
            {renderTags()}
            {renderTitle()}
            {renderInfo('date')}
            {renderSettings()}
          </div>
        </ContentList>
      </React.Fragment>
    )
  }

  function renderContentByView() {
    switch (props.view) {
      case CardView.Tiled:
        return renderTiledView();
      case CardView.List:
        return renderListView();
      case CardView.ShortList:
        return renderShortListView();
      default:
        return renderTiledView();
    }
  }

  return (
    <React.Fragment>
      <Container
        variant={props.variant}
        view={props.view}
        opened={props.opened}
      >
        {renderContentByView()}
      </Container>
      {props.studentsModalOpened && (
        <ModalCourseStudents
          course={props}
          close={props.studentModalClose}
        />
      )}
    </React.Fragment>
  );
}

CoursePreview.defaultProps = {
  variant: CoursePreviewVariant.Default,
};

export default CoursePreview;
